import React from "react";
import { connect } from "react-redux";
import { selectActiveProject, selectBasemap, selectAllFundData } from "../../reducers";
import PropTypes from "prop-types";
import { projectShape } from "../../modules/project";
import { setActiveProject, setIsMultiple } from "../../actions/ui";
import styles from "./project-popup.module.scss";
import { StaticMap, Marker } from "react-map-gl";
import streetsMapStyle from "../map/streets-map-style.json";
import satelliteMapStyle from "../map/sattelite-map-style.json";
import ImageCarousel from "../image-carousel";
import { CloseButton } from "../ui/close-button/CloseButton";
import { fields } from "../../modules/fields";
import FocusLock from "react-focus-lock";

const MARKER_STYLE = {
    width: 24,
    height: 24,
    transform: "translate(-50%, -100%)",
};
const MARKER_STYLE_MULTIPLE = {
    width: 32,
    height: 48,
    transform: "translate(-50%, -100%)",
};

const format = (value, formatType) => {
    switch (formatType) {
        case "dollars":
            if (typeof value === "string") {
                return value;
            } else {
                return "$" + value.toLocaleString();
            }
        case "title case":
            if (typeof value === "string") {
                return value.replace(/\w\S*/g, function (txt) {
                    return (
                        txt.charAt(0).toUpperCase() +
                        txt.slice(1, txt.length).toLowerCase()
                    );
                });
            } else {
                return value;
            }
        default:
            return value;
    }
};

const a11yFormat = (value, formatType) => {
    switch (formatType) {
        case "dollars":
            if (typeof value === "string") {
                return value + " dollars";
            } else {
                return value.toLocaleString() + " dollars";
            }
        default:
            return value;
    }
};

const ProjectPopup = ({
    activeProject,
    setActiveProject,
    setIsMultiple,
    basemap,
    fundData,
}) => {
    if (!activeProject) return null;

    const fundObj = fundData[activeProject.fundName];
    const projectIcon = fundObj ? fundObj.icon : "";
    const fundName = fundObj ? fundObj.displayName : "";

    return (
        <div onClick={() => setActiveProject(null)} className={styles.overlay}>
            <FocusLock className={styles.focusLock}>
                <div onClick={e => e.stopPropagation()} className={styles.projectPopup}>
                    <div className={styles.iconAndName}>
                        <img
                            className={styles.markerIcon}
                            src={projectIcon}
                            alt={`Marker icon for: ${fundName}`}
                        />{" "}
                        <h3 tabIndex={0}>Funding Source: {fundName}</h3>
                    </div>
                    <CloseButton
                        onClick={() => setActiveProject(null)}
                        ariaLabel={"Close project details pop-up."}
                    />
                    {activeProject["Reserve Name"] && (
                        <div className={styles.reserveNameBlock}>
                            <div className={styles.reservePinWrapper}>
                                <img
                                    src={`${process.env.PUBLIC_URL}/img/red-pin.svg`}
                                    alt="Pin"
                                    className={styles.reserveNamePin}
                                />
                            </div>
                            <h5 className={styles.reserveName} tabIndex={0}>
                                {activeProject["Reserve Name"]}
                            </h5>
                        </div>
                    )}
                    {activeProject.shortDescription && (
                        <h4>{activeProject.shortDescription}</h4>
                    )}
                    <p tabIndex={0}>{activeProject["Description"]} </p>
                    {fields
                        .filter(field => activeProject[field.key] && field.popupView)
                        .map(field => {
                            const formattedVal = format(
                                activeProject[field.key],
                                field.format
                            );
                            return (
                                <p
                                    key={field.key}
                                    tabIndex={0}
                                    className={
                                        field.visuallyHidden ? "visually-hidden" : ""
                                    }
                                >
                                    <label>{field.label}</label>
                                    <span
                                        aria-label={a11yFormat(
                                            activeProject[field.key],
                                            field.format
                                        )}
                                    >
                                        {formattedVal}
                                    </span>
                                </p>
                            );
                        })}
                    {activeProject["Multiple locations"] === "Y" && (
                        <p>
                            <label>Project is in multiple locations</label>
                            <span className={styles.multipleProjects}>
                                Project locations are a central point within the Local
                                Government Area and not necessarily the actual delivery
                                location of the project.
                            </span>
                        </p>
                    )}
                    {activeProject["Multiple locations"] === "Y" &&
                        activeProject["Multiple LGAs"] === "Y" && (
                            <button
                                className={styles.multipleButton}
                                onClick={e => setIsMultiple(activeProject)}
                            >
                                View all project locations &#x203A;
                            </button>
                        )}
                    <ImageCarousel
                        projectName={activeProject.Description}
                        project={activeProject}
                    />

                    <div
                        className={styles.mapWrapper}
                        tabIndex={0}
                        aria-label={`Map showing current project location at latitude: ${activeProject.Latitude}, longitude: ${activeProject.Longitude}.`}
                    >
                        <StaticMap
                            mapboxApiAccessToken={process.env.REACT_APP_MAPBOX_API_TOKEN}
                            width={"100%"}
                            height={200}
                            zoom={10}
                            mapStyle={
                                basemap === "ROADMAP"
                                    ? streetsMapStyle
                                    : satelliteMapStyle
                            }
                            longitude={parseFloat(activeProject.Longitude)}
                            latitude={parseFloat(activeProject.Latitude)}
                        >
                            <Marker
                                longitude={parseFloat(activeProject.Longitude)}
                                latitude={parseFloat(activeProject.Latitude)}
                            >
                                <img
                                    style={
                                        activeProject["Multiple locations"] === "Y"
                                            ? MARKER_STYLE_MULTIPLE
                                            : MARKER_STYLE
                                    }
                                    alt="pin"
                                    src={
                                        activeProject["Multiple locations"] === "Y"
                                            ? "img-multiple" + projectIcon
                                            : projectIcon
                                    }
                                />
                            </Marker>
                        </StaticMap>
                    </div>
                </div>
            </FocusLock>
        </div>
    );
};

const mapStateToProps = state => ({
    activeProject: selectActiveProject(state),
    basemap: selectBasemap(state),
    fundData: selectAllFundData(state),
});
const mapDispatchToProps = {
    setActiveProject,
    setIsMultiple,
};
ProjectPopup.propTypes = {
    activeProject: PropTypes.shape(projectShape),
    setIsMultiple: PropTypes.func.isRequired,
    setActiveProject: PropTypes.func.isRequired,
    basemap: PropTypes.string.isRequired,
    fundData: PropTypes.object.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(ProjectPopup);



// WEBPACK FOOTER //
// ./src/components/project-popup/project-popup.js